import { invariant } from '@mimir/common/util/invariant';
import type { TimelineInternalOps } from '../timelineInternalOps';
import { addVideoItemsToMainTrack } from './ops/addVideoItemsToMainTrack';
import type { TimelineAction } from './timelineActions';
import { setOutPointForSource } from './ops/setOutPointForSource';
import type { AbortableOp } from '../abortableOp';
import { setInPointForSource } from './ops/setInPointForSource';
import { moveMainTrackBox } from './ops/moveMainTrackBox';
import { insertMainTrackObjects } from './ops/insertVideoItemsInMainTrack';
import { deleteBox } from './ops/deleteBox';

/**
 * This class is responsible for adding, undoing and redoing timeline
 * actions. It supports abortable actions that must be either aborted
 * or completed (at which point they are converted into regular actions).
 */
export class ActionController {

  /**
   * The list of all actions that have been applied to go from an empty timeline
   * to the current one.
   */
  private readonly actions: TimelineAction[] = [];

  /**
   * The index of the last action that was executed or -1 if no action has been
   * applied yet. Note that this number can be lower than actions.length since
   * we may have gone back with undo.
   */
  private lastActionIndex: number = -1;

  private currentAbortableOp: AbortableOp | null = null;


  constructor(
    private readonly timelineHelper: TimelineInternalOps,
  ) {}


  getLastActionIndex() {
    return this.lastActionIndex;
  }


  setAbortableAction(op: AbortableOp) {
    invariant(this.currentAbortableOp === null, 'Cannot set abortable action if there is already one');
    this.currentAbortableOp = op;
    this.updateUndoRedoStatus();
  }


  getCurrentAbortableOp(): AbortableOp | null {
    return this.currentAbortableOp;
  }


  abort(): void {
    if (this.currentAbortableOp !== null) {
      this.currentAbortableOp.abort();
      this.currentAbortableOp = null;
      this.updateUndoRedoStatus();
    }
  }


  completeCurrentOperation(): void {
    if (this.currentAbortableOp !== null) {
      // For undo to work as expected, the action
      // needs to be executed on the state before the user
      // started the abortable operation, which is why we
      // invoke abort() first to get into that state
      this.currentAbortableOp.abort();
      const action = this.currentAbortableOp.action;
      this.currentAbortableOp = null;
      if (action !== null) {
        this.addAction(action);
      }
    }
    this.updateUndoRedoStatus();
  }


  addAction(action: TimelineAction) {
    // For now, when adding an action, we truncate the actions past this point, if any, to simply
    // remove undone actions that were still redoable
    this.actions.splice(this.lastActionIndex + 1, this.actions.length);
    this.lastActionIndex = this.actions.length - 1;
    this.actions.push(action);

    // Now, let's actually execute the action
    this.executeNextAction();
  }


  /**
   * Executes the next action that comes after the last action index, if any.
   * This update the ui state as well as the last action index.
   */
  private executeNextAction() {
    const actionIndex = this.lastActionIndex + 1;
    if (actionIndex >= this.actions.length) {
      // Nothing to do
      return;
    }

    const action = this.actions[actionIndex];
    this.doAction(action);
    this.lastActionIndex = actionIndex;

    this.updateUndoRedoStatus();
  }


  private doAction(action: TimelineAction) {
    const actionType = action.type;
    switch (actionType) {
      case 'AddVideoItemsToMainTrackAction': addVideoItemsToMainTrack(this.timelineHelper, action); break;
      case 'SetOutPointForSourceAction': setOutPointForSource(this.timelineHelper, action); break;
      case 'SetInPointForSourceAction': setInPointForSource(this.timelineHelper, action); break;
      case 'MoveMainTrackBoxAction': moveMainTrackBox(this.timelineHelper, action); break;
      case 'InsertMainTrackObjectsAction': insertMainTrackObjects(this.timelineHelper, action); break;
      case 'DeleteBoxAction': deleteBox(this.timelineHelper, action); break;

      // This line makes the compiler report an error if a new value is added without being
      // handled in the switch
      default: actionType satisfies never;
    }

  }


  private undoAction(action: TimelineAction) {
    invariant(action.undo !== undefined, () => `Should have undo() for action ${JSON.stringify(action)}`);
    action.undo();
    action.undo = undefined;
  }


  private canUndo() {
    return this.currentAbortableOp === null && this.lastActionIndex > -1;
  }


  private canRedo() {
    return this.currentAbortableOp === null && (this.lastActionIndex + 1) < this.actions.length;
  }


  undo() {
    invariant(this.canUndo(), () => 'Unexpected undo()');

    const actionToUndo = this.actions[this.lastActionIndex];
    this.undoAction(actionToUndo);
    this.lastActionIndex--;

    this.updateUndoRedoStatus();
  }


  redo() {
    invariant(this.canRedo(), () => 'Unexpected redo()');
    // executeNextAction already invoke updateUndoRedoStatus(),
    // no need to do it here again
    this.executeNextAction();
  }


  private updateUndoRedoStatus() {
    this.timelineHelper.updateUndoRedoStatus(this.canUndo(), this.canRedo());
  }

}
